/**
 * Interface which describes the shape of an object which
 * contains per-field validation errors.
 */
export interface ValidationErrors {
  [field: string]: string[];
}

/**
 * Creates a validation errors object which encapsulates errors from an existing validation
 * errors object as well as errors for the given property.
 * @param propName The name of the property whose errors should be added.
 * @param errorMessages The error messages to add.
 * @param errors If defined the returned object will contain
 */
export function addPropertyErrors(propName: string, errorMessages: string[], errors?: ValidationErrors) {
  const errObj = errors || {};

  return Object.assign({}, errObj, {
    [propName]: [...errorMessages],
  });
}

export interface ValidationErrorState {
  errors?: ValidationErrors;
  isValid: boolean;
}

/**
 * Gets the current error state.
 * @param errors The validation errors
 */
export function getErrorState(errors: ValidationErrors | undefined) {
  let isValid = true;
  if (errors) {
    for (const property in errors) {
      if (errors.hasOwnProperty(property)) {
        isValid = false;
        break;
      }
    }
  }

  const errorState: ValidationErrorState = { errors, isValid };
  return errorState;
}

/**
 * Creates a validation errors object which encapsulates errors from an existing validation
 * errors object excluding errors for a specified property.
 * @param propName The name of the property to remove from the validation errors object.
 * @param errors A validation errors object.
 */
export function removePropertyErrors(propName: string, errors?: ValidationErrors) {
  let retval = errors || {};

  if (retval[propName]) {
    retval = Object.assign({}, retval);
    delete retval[propName];
  }

  return retval;
}

/**
 * Error object which is returned from a failed hybrid operation.
 */
export class SimManagementError extends Error {
  private _validationErrors?: ValidationErrors;
  private _statusCode?: number;

  constructor(message?: string, statusCode?: number) {
    super(message);
    this._statusCode = statusCode;
  }

  /**
   * Gets any validation issues associated with the error.
   */
  public get validationErrors(): ValidationErrors | undefined {
    return this._validationErrors;
  }

  /**
   * Sets the validation issues associated with the error.
   */
  public set validationErrors(value: ValidationErrors | undefined) {
    this._validationErrors = value;
  }

  public get statusCode(): number | undefined {
    return this._statusCode;
  }

  /**
   * Gets any standard HTTP errors.
   * @param error
   */
  public getStandardHttpError() {
    if (this.message === 'Request failed with status code 400') {
      return 'bad request';
    }

    if (this.message === 'Request failed with status code 401') {
      return 'unauthorized';
    }

    if (this.message === 'Request failed with status code 403') {
      return 'forbidden';
    }

    if (this.message === 'Request failed with status code 404') {
      return 'not found';
    }

    if (this.message === 'Request failed with status code 500') {
      return 'unexpected error';
    }
  }
}
